// NOTE: please keep this JSON config sorted alphabetically to keep consistency between region config files

import {
  PaymentMethod,
  SUPPORTED_COUNTRY,
  SUPPORTED_LANGUAGE,
  MaxAgeExpiration,
} from 'utils/constants';
import { OrderSearchOperationName } from 'ui/forms/OrderLookUp/types';
import { commonConfig } from 'sites/common/config';

import { ComponentType, SiteConfig } from '../types';

import { JPForms } from './forms';
import { JPFieldProps } from './fields';
import { jpStates } from './states';

export const config: SiteConfig = {
  ...commonConfig,
  absoluteLinkBreadcrumb: true,
  additionalCtaClass: 'px-[12px] text-[12px] !whitespace-nowrap',
  addressCountries: [{ label: 'Japan', value: SUPPORTED_COUNTRY.JP }],
  addressFormat: [
    ['lastName', 'firstName'],
    ['lastNamePronunciation', 'firstNamePronunciation'],
    ['postalCode', 'stateCode'],
    ['city', 'address1', 'address2'],
  ],
  addressFormatOneLiner:
    '@{lastName} @{firstName}, @{lastNamePronunciation} @{firstNamePronunciation} - @{postalCode} @{stateCode}, @{city} @{address1} @{address2}',
  addressSearch: {
    billing: ['JP'],
    shipping: ['JP'],
  },
  addressStates: { JP: jpStates },
  amazonPayButtonOptions: {
    color: 'LightGray',
    size: 'medium',
  },
  amazonPayScriptUrl: 'https://static-fe.payments-amazon.com/checkout.js',
  amazonWidget: {
    countryCode: 'fe',
    widgetSource: 'lpa/js',
  },
  analytics: { releaseVersion: `Japan | ${process.env.VERSION}` },
  applePaySupportedNetworks: [],
  badgeIdsExcludedBySaleStatus: ['new', 'exclusive', 'worn_by'],
  branchAnalyticsData: {
    agencyId: '1006963764752306474',
  },
  charactersNeededToSubmitSearch: 1,
  colors: {
    その他: 'other',
    イエロー: 'yellow',
    オレンジ: 'orange',
    グリーン: 'green',
    グレー: 'gray',
    ゴールド: 'gold',
    シルバー: 'silver',
    パープル: 'purple',
    ピンク: 'pink',
    ブラウン: 'brown',
    ブラック: 'black',
    ブルー: 'blue',
    ベージュ: 'tan',
    ホワイト: 'white',
    マルチカラー: 'multi-colored',
    メタリック: 'metallic',
    レッド: 'red',
  },
  cookieConsent: {
    documentLanguage: true,
    provider: 'OneTrust',
  },
  countryCode: 'jp',
  countryName: '日本/Japan',
  currency: {
    code: 'JPY',
    decimalsToDisplay: 0,
    symbol: '￥',
  },
  dateFormatWithWeekDay: ['YYYY', 'TY', 'MM', 'TM', 'DD', 'TD', '(', 'TW', ')'],
  defaultPaymentMethod: PaymentMethod.CREDIT_CARD,
  deliveryTime: [
    {
      deliveryTimeCode: '0',
      deliveryTimeDisplayText: '指定なし(最短でのお届けとなります)',
    },
    {
      deliveryTimeCode: '1',
      deliveryTimeDisplayText: '午前中',
    },
    {
      deliveryTimeCode: '4',
      deliveryTimeDisplayText: '12:00 - 14:00',
    },
    {
      deliveryTimeCode: '5',
      deliveryTimeDisplayText: '14:00 - 16:00',
    },
    {
      deliveryTimeCode: '6',
      deliveryTimeDisplayText: '16:00 - 18:00',
    },
    {
      deliveryTimeCode: '7',
      deliveryTimeDisplayText: '18:00 - 20:00',
    },
    {
      deliveryTimeCode: '8',
      deliveryTimeDisplayText: '19:00 - 21:00',
    },
  ],
  fieldProps: JPFieldProps,
  flag: '🇯🇵',
  forms: { ...JPForms },
  maxAgeCookiesExpires: MaxAgeExpiration.DAILY,
  maxCountRecordKeeper: 100,
  miniCartItemType: ComponentType.SECONDARY,
  myAccountDashboardConfiguration: [
    {
      childOrder: [
        'membershipComponent',
        'extendedProfileComponent',
        'preferencesComponent',
        'addressBookComponent',
        'ambassadorComponent',
      ],
      name: 'leftColumn',
    },
    {
      childOrder: [
        'passwordComponent',
        'paymentInformationComponent',
        'orderHistoryComponent',
      ],
      name: 'rightColumn',
    },
  ],
  navCategoryMaxColumnCount: 6,
  newsletterProvider: 'Cheetah',
  orderLookUpQuery: OrderSearchOperationName['SearchOrderByEmailPhone'],
  orderedNewsletterSignupFormFields: {
    address: ['zip-code', 'city', 'address'],
    name: ['lastName', 'firstName'],
  },
  orderedPaymentPartners: [
    'visa',
    'master',
    'JCB',
    'americanExpress',
    'diners',
    'amazonPay',
    'payPay',
  ],
  paymentCreditCardErrors: {
    '100': 'カード番号」を入力してください(半角英数)[エラーコード:100]',
    '101': 'カード番号」を入力してください(半角英数)[エラーコード:101]',
    '102': 'カード番号」を入力してください(半角英数)[エラーコード:102]',
    '110': '「有効期限」を入力してください[エラーコード:110]',
    '111': '「有効期限」を入力してください[エラーコード:111]',
    '112': '「有効期限」を入力してください[エラーコード:112]',
    '113': '「有効期限」を入力してください[エラーコード:113]',
    '121':
      '「セキュリティコード」を入力してください(3桁または4桁の半角数字)[エラーコード:121]',
    '122':
      '「セキュリティコード」を入力してください(3桁または4桁の半角数字)[エラーコード:122]',
    '131':
      '「カード名義人」を入力してください(半角英数50字以内)[エラーコード:131]',
    '132':
      '「カード名義人」を入力してください(半角英数50字以内)[エラーコード:132]',
    '42C010000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C030000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C120000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C130000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C140000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C150000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C500000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C510000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C530000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C540000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C550000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C560000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C570000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C580000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C600000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C700000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C710000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C720000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C730000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C740000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C750000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C760000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C770000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42C780000':
      '決済処理に失敗しました。申し訳ございませんが、しばらく時間をあけて購入画面からやり直してください。',
    '42G020000':
      'カード残高が不足しているために、決済を完了する事が出来ませんでした。',
    '42G030000':
      'カード限度額を超えているために、決済を完了する事が出来ませ んでした。',
    '42G040000':
      'カード残高が不足しているために、決済を完了する事が出来ませんでした。',
    '42G050000':
      'カード限度額を超えているために、決済を完了する事が出来ませんでした。',
    '42G120000': 'このカードでは取引をする事が出来ません。',
    '42G220000': 'このカードでは取引をする事が出来ません。',
    '42G300000': 'このカードでは取引をする事が出来ません。',
    '42G420000':
      '暗証番号が誤っていた為に、決済を完了する事が出来ませんでした。',
    '42G440000':
      'セキュリティーコードが誤っていた為に、決済を完了する事が出来ませんでした。',
    '42G450000':
      'セキュリティーコードが入力されていない為に、決済を完了する事が出来ませんでした。',
    '42G540000': 'このカードでは取引をする事が出来ません。',
    '42G550000':
      'カード限度額を超えているために、決済を完了する事が出来ませんでした。',
    '42G560000': 'このカードでは取引をする事が出来ません。',
    '42G600000': 'このカードでは取引をする事が出来ません。',
    '42G610000': 'このカードでは取引をする事が出来ません。',
    '42G650000':
      'カード番号に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G670000':
      '商品コードに誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G680000': '金額に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G690000':
      '税送料に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G700000':
      'ボーナス回数に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G710000':
      'ボーナス月に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G720000':
      'ボーナス額に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G730000':
      '支払開始月に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G740000':
      '分割回数に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G750000':
      '分割金額に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G760000':
      '初回金額に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G770000':
      '業務区分に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G780000':
      '支払区分に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G790000':
      '照会区分に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G800000':
      '取消区分に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G810000':
      '取消取扱区分に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G830000':
      '有効期限に誤りがあるために、決済を完了する事が出来ませんでした。',
    '42G950000': 'このカードでは取引をする事が出来ません。',
    '42G960000': 'このカードでは取引をする事が出来ません。',
    '42G970000': 'このカードでは取引をする事が出来ません。',
    '42G980000': 'このカードでは取引をする事が出来ません。',
    '42G990000': 'このカードでは取引をする事が出来ません。',
    '501':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:501]',
    '502':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:502]',
    '511':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:511]',
    '512':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:512]',
    '521':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:521]',
    '522':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:522]',
    '531':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:531]',
    '541':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:541]',
    '551':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:551]',
    '552':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:552]',
    '553':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:553]',
    '901':
      '申し訳ございません。システム処理が正しく完了しませんでした。もう一度試していただくか、カスタマーサービスまでご連絡ください。[エラーコード:901]',
    E01230006: 'カード登録連番に数字以外の文字が含まれています。',
    E01230009: 'カード登録連番が最大登録可能数を超えています。',
    general:
      '申し訳ございませんが、何らかの理由によりご注文を完了することができませんでした。',
  },
  paymentMethodsMixedWithGiftCard: [
    PaymentMethod.AMBASSADOR_CARD,
    PaymentMethod.SCHEME,
    PaymentMethod.ADYEN_PAYPAL,
    PaymentMethod.AFTERPAY_TOUCH,
    PaymentMethod.ZIP_PAY,
    PaymentMethod.KLARNA,
    PaymentMethod.KLARNA_ACCOUNT,
    PaymentMethod.ALIPAY,
    PaymentMethod.WECHAT_PAY,
  ],
  pdpLowInventoryThreshold: 5,
  pixleeId: {
    ja: '1456269',
  },
  plpShowDefaultSortOption: true,
  previewContentLanguage: 'ja',
  priceInputMaxLength: 6,
  priceSliderInterval: 1000,
  pumaAppDownloadUrl: 'https://app.puma.com/web-download-jp',
  region: 'asia',
  regionCode: 'JPN',
  resetSearchConditionsOptions: {
    isRouterScrollEnabled: true,
    showResetSearchConditionsButton: true,
  },
  reviewFormModalSupportLinks: [
    {
      id: '1',
      link: 'https://pumajapan.ugc.bazaarvoice.com/content/8206-ja_jp/termsandconditions.htm',
      popup: true,
      title: 'termsAndConditions',
    },
    {
      id: '2',
      link: '/help/privacy-policy',
      newtab: true,
      title: 'privacyPolicy',
    },
    {
      id: '3',
      link: 'https://pumajapan.ugc.bazaarvoice.com/content/8206-ja_jp/guidelines.htm',
      popup: true,
      title: 'reviewGuidelines',
    },
  ],
  savedCreditCardTemplate: ['saved', 'new'],
  scheduledArrivalDateFormat: [
    'TEXT',
    ' ',
    'YYYY',
    'TY',
    ' ',
    'MM',
    'TM',
    ' ',
    'DD',
    'TD',
    ' ',
    '(',
    'TW',
    ')',
  ],
  searchEngineIndexableLanguages: [SUPPORTED_LANGUAGE.JA],
  searchSymbolsMinValue: 1,
  selectablePaymentMethods: [
    PaymentMethod.COD,
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.PAY_PAY,
    PaymentMethod.AMAZON_PAY_V2,
    PaymentMethod.AMBASSADOR_CARD,
  ],
  shoppingCartItemType: ComponentType.SECONDARY,
  socialMediaLinks: {
    instagram: 'https://instagram.com/pumajapan/',
    twitter: 'https://x.com/puma_JPN',
    youtube: 'https://youtube.com/@PumaJPN',
  },
  staticFeatures: {
    ...commonConfig.staticFeatures,
    addAddressResultErrorDisabled: true,
    allowEditIncorrectSavedAddressCheckout: true,
    allowGuestAccessToOrderDetails: true,
    ambassadorCardsEnabled: true,
    canRemoveWishlistItemsFromPDP: true,
    checkoutHideShippingAndReturnsMessage: true,
    checkoutShowSaveCardCheckbox: true,
    displayPromoAppliedPromotion: true,
    displayPromoDiscountBreakdown: true,
    editPasswordResultErrorDisabled: true,
    enableStockStatusInPDP: true,
    hideBulletsListsPdp: true,
    hideCalloutMessage: true,
    hideEanColorCode: true,
    hideMyAccountSubtitle: true,
    hideNewsletterSignupButtonOnMobile: true,
    hidePaymentInformationOnAccountPage: true,
    hideTermsOnLoginForm: true,
    isGmoEnabled: true,
    mixedCart: true,
    pdpDisableReturns: true,
    pdpShowStockMessageBySFCCLogic: true,
    popoverLoginEnabled: true,
    popoverLoginEnabledJP: true,
    productFilterCollapsible: true,
    redirectToAccountDashboardAfterProfileUpdate: true,
    returnsOnOrderDetailsPage: true,
    returnsPageDisabled: true,
    reviewOrderOnCheckout: true,
    searchLocalImagesOnDropCalendar: true,
    searchLocalImagesOnProductComparison: true,
    setAutoCapitalizeOff: true,
    showAddAddressButtonInAccountDashboard: true,
    showAllPricesAtOnce: true,
    showBreadcrumbsOnOrderDetails: false,
    showCookieBanner: false,
    showCookieConsentLayer: true,
    showFinancialIncentivesLink: false,
    showFreeShippingCost: true,
    showGiftWithPurchaseModalInMobile: true,
    showHeaderSubHeaderProductDetail: false,
    showMetricAsDefault: true,
    showModifiedResetPassFormPopup: true,
    showNewsletterSignupOnOrderConfirmation: true,
    showOneFinalPriceOnPurchasedProduct: true,
    showPaymentPartnersInFooter: true,
    showPhoneInCheckout: false,
    showPriceDifference: false,
    showProductRecommenderOnError: true,
    showProductStoryKeywordTitle: true,
    showPromoExclusionMsgAboveProductDetails: true,
    showPromoExclusionMsgInGray: true,
    showPumaCard: true,
    showRefundInformationAtOrder: true,
    showSaleBadgeOnProductImageEnabled: true,
    showSearchFormOnError: true,
    showSearchResult: true,
    showSearchTipsOnError: true,
    showShippingDiscountInCheckout: true,
    showTitleTranslationInAccountOrderHistory: true,
    showWishlistIconBadgeOnDropdownMenu: true,
    singInInConfirmationForm: true,
    styleBazaarVoiceModal: true,
    translateToJapanese: true,
    useDefaultJapanChart: true,
    useNewPaymentForm: false,
    useProductSizeChart: true,
    useSFCCDividerForSEODescription: true,
    useStockInfoRedesign: true,
    useStockMessageFromResponse: true,
    useVariantPromotionExclusion: true,
  },
  supportedLocales: ['ja-jp'],
  validation: {
    address: {
      amazonAddressMaxLength: 48,
      length: commonConfig.validation.address.length,
      line1: '^[\u0000-~-ž’”　-〿぀-ゟ゠-ヿ＀-￯一-龯★-☆←-↕※]+$',
      line2: '^[\u0000-~-ž’”　-〿぀-ゟ゠-ヿ＀-￯一-龯★-☆←-↕※]+$',
    },
    city: '^[\u0000-~-ž’”　-〿぀-ゟ゠-ヿ＀-￯一-龯★-☆←-↕※]+$',
    email: '^[\\w-\\.\\%\\+]+@(([\\w-]{1,})+\\.)+[\\w-]{2,6}$',
    holderName: {
      regex: "^[a-zA-Z0-9.'\\s-]+$",
    },
    name: {
      length: commonConfig.validation.name.length,
      regex: '^([\u0000-~-ž’”]|[　-〿぀-ゟ゠-ヿ＀-￯一-龯])+$',
    },
    password: '^(?=.*[a-zA-Z])(?=.*[0-9]).{8,255}$',
    phone: {
      length: {
        max: 30,
        min: commonConfig.validation.phone.length.min,
      },
      regex:
        '^[\\+(/. 0-9 \\)-]+[ ]*((( |x|ext|ext.|extension|Ext|Ext.|Extension|#){1}[ ]?([0-9]){1,7}){0,1})$',
    },
    postalCode: {
      JP: '^([0-9]{3}-[0-9]{4})$|^([0-9]{7})$',
    },
    postalCodeMaxLength: 8,
  },
};
